/* -----------------Icons--------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppTheme } from "@hooks/useAppTheme";
import { smartPluralize } from "@memorang/helpers/src/common";
import { Confidence } from "@memorang/types/src/session/common";

/* -----------------Components--------------- */
import type { RenderableItemTypes } from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import { StatItem } from "@memorang/ui/src/components/StatItem";
import type { IconType } from "@memorang/ui/src/types";
import RowStats from "components/RowStats";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { learningModeIcon } from "./helper";
/* -----------------Props--------------- */
type Props = {
	timeTaken: number;
	confidence?: Confidence;
	numAnsweredStat?: string;
	correct?: boolean;
	pointsEarnedStatValue?: string;
	itemType?: RenderableItemTypes;
	isAiItemType?: boolean;
	numberOfWords?: number;
	showNumAttempts?: boolean;
	xpEarned?: number;
	attemptsStatValue?: string;
	xpLabel?: string;
	numResponses?: number;
	showXpEarned?: boolean;
};

const getIcon = (name: string, color: string) => (
	<Icon name={name as IconType} color={color} size={24} />
);

/* -----------------Component--------------- */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const PostAnswerStats = (props: Props) => {
	const {
		confidence,
		timeTaken,
		correct,
		pointsEarnedStatValue,
		itemType,
		isAiItemType,
		numberOfWords,
		showNumAttempts,
		xpEarned,
		attemptsStatValue,
		xpLabel,
		numResponses,
		showXpEarned,
	} = props;
	const theme = useAppTheme();
	const { t } = useTranslation();

	const getConfidenceLabel = (confidence: Confidence) => {
		switch (confidence) {
			case Confidence.high:
				return t("session.confidence.high");
			case Confidence.medium:
				return t("session.confidence.medium");
			case Confidence.low:
				return t("session.confidence.low");
			default:
				return "";
		}
	};

	const confidenceData = useMemo(() => {
		switch (confidence) {
			case Confidence.high:
				return {
					text: "High",
					icon: getIcon("emoticon-happy-outline", theme.colors.confidence.high),
				};
			case Confidence.low:
				return {
					text: "Low",
					icon: getIcon("emoticon-sad-outline", theme.colors.confidence.low),
				};
			default:
				return {
					text: "Medium",
					icon: getIcon(
						"emoticon-neutral-outline",
						theme.colors.confidence.medium,
					),
				};
		}
	}, [
		confidence,
		theme.colors.confidence.high,
		theme.colors.confidence.low,
		theme.colors.confidence.medium,
	]);

	const skill = useMemo(() => {
		switch (itemType) {
			case "AUDIO_INPUT":
				return "Speaking";
			case "WRITING_INPUT":
				return "Writing";
			default:
				return null;
		}
	}, [itemType]);

	const list = [
		...(skill
			? [
					{
						icon: getIcon(
							learningModeIcon[skill.toLowerCase()],
							theme.colors.onSurfaceVariant,
						),
						label: t("session.stats.skill"),
						value: skill,
					},
				]
			: []),
		// biome-ignore lint/style/noNegationElse: to keep it uniform
		...(!isAiItemType
			? [
					{
						icon: correct ? "thumb-up-outline" : "thumb-down-outline",
						label: t("session.stats.answered"),
						value: correct
							? t("session.answer.correct")
							: t("session.answer.incorrect"),
						iconColor: correct ? theme.colors.success.main : theme.colors.error,
					},
				]
			: []),
		...(itemType === "WRITING_INPUT"
			? [
					{
						icon: "text-box-check-outline",
						label:
							numberOfWords === 1
								? t("session.stats.word")
								: t("session.stats.words"),
						value: numberOfWords?.toString(),
						iconColor: theme.colors.onSurfaceVariant,
					},
				]
			: [
					...(showXpEarned
						? []
						: [
								{
									icon: "timer-outline",
									label: t("session.stats.speed"),
									value: t("session.stats.time_taken", {
										time: timeTaken,
										sec: smartPluralize("sec", timeTaken),
									}),
									iconColor: theme.colors.onSurfaceVariant,
								},
							]),
				]),
		...(showXpEarned
			? [
					{
						icon: getIcon(
							"star-four-points-outline",
							theme.palette.yellow.light,
						),
						label: xpLabel || "PTS EARNED",
						value: `${xpEarned} XP`,
					},
				]
			: []),
		...(showNumAttempts
			? [
					{
						icon: getIcon("bullseye-arrow", theme.palette.error.main),
						label: "Attempt",
						value: attemptsStatValue,
					},
				]
			: []),
		...(confidence != null && !isAiItemType
			? [
					{
						icon: confidenceData.icon,
						label: t("session.stats.confidence"),
						value: getConfidenceLabel(confidence),
					},
				]
			: []),
		...(pointsEarnedStatValue
			? [
					{
						icon: "star-outline",
						label: t("session.stats.pointsEarned"),
						value: pointsEarnedStatValue,
						iconColor: theme.colors.onSurfaceVariant,
					},
				]
			: []),
	];

	if (attemptsStatValue) {
		const stats = [
			{
				icon: "sword-cross",
				label: "Challengers",
				value: String(numResponses),
				color: theme.palette.success.dark,
			},
			// {
			// 	label: "Ranking",
			// 	value: "Top 10%",
			// 	icon: "podium-gold",
			// 	color: theme.palette.info.main,
			// },
			{
				label: "Points",
				value: `+${xpEarned} XP`,
				icon: "star-outline",
				color: theme.palette.yellow.light,
			},
		];
		return <RowStats stats={stats} itemWidth="10%" />;
	}

	return (
		<Box flexDirection="row" alignItems="center" justifyContent="space-around">
			{list.map(({ icon, label, value, iconColor }) => (
				<Box key={label} flexDirection="row" flex={1} justifyContent="center">
					<StatItem
						icon={icon}
						value={value}
						overlineText={label}
						color={iconColor}
						stacked
					/>
				</Box>
			))}
		</Box>
	);
};

export default PostAnswerStats;
